import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Building2, Briefcase, GraduationCap, UserCog, Users, Plus, Pencil, Trash2, ImagePlus, X, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ENTITIES = [
  { key: 'programs', label: 'Programs', icon: Building2, singular: 'Program' },
  { key: 'instructors', label: 'Instructors', icon: UserCog, singular: 'Instructor' },
  { key: 'skills-trainers', label: 'Skills Trainers', icon: GraduationCap, singular: 'Skills Trainer' },
  { key: 'job-sites', label: 'Job Sites', icon: Briefcase, singular: 'Job Site' },
  { key: 'interns', label: 'Interns', icon: Users, singular: 'Intern' },
];

const blankForm = (key) => {
  switch (key) {
    case 'programs': return { school_name: '', photo_url: '', city: '', state: '', lead_contact: '', phone: '', program_types: [], project_search: 'No' };
    case 'job-sites': return { name: '', program_id: '', contact: '', address: '', phone: '', description: '', photo_url: '', tasks: [] };
    case 'instructors': return { name: '', email: '', program_id: '', photo_url: '' };
    case 'skills-trainers': return { name: '', email: '', program_id: '', job_site_ids: [], photo_url: '' };
    case 'interns': return { first_name: '', last_name: '', email: '', dob: '', case_study_notes: '', dietary_restriction: '', physical_restrictions: '', preferences: '', disability_category: '', program_id: '', job_site_id: '', instructor_id: '', skills_trainer_id: '', rotation_1_job_site_id: '', rotation_2_job_site_id: '', rotation_3_job_site_id: '', photo_url: '' };
    default: return {};
  }
};

const NONE = '__none__';
const PROGRAM_TYPES = ['K-12', 'Transition', 'Adult', 'Residential', 'Other'];
const DISABILITY_CATEGORIES = [
  'Autism',
  'Deaf-Blindness',
  'Deafness',
  'Emotional Disturbance',
  'Hearing Impairment',
  'Intellectual Disability',
  'Multiple Disabilities',
  'Orthopedic Impairment',
  'Other Health Impairment',
  'Specific Learning Disability',
  'Speech or Language Impairment',
  'Traumatic Brain Injury',
  'Visual Impairment (including Blindness)',
  'Developmental Delay',
];

const COMPLEXITY = {
  1: { label: 'Level 1 · Simple', chip: 'bg-emerald-100 text-emerald-700 border-emerald-300', dot: 'bg-emerald-500', solid: 'bg-emerald-500 text-white border-emerald-500' },
  2: { label: 'Level 2 · Moderate', chip: 'bg-amber-100 text-amber-700 border-amber-300', dot: 'bg-amber-500', solid: 'bg-amber-500 text-white border-amber-500' },
  3: { label: 'Level 3 · Complex', chip: 'bg-rose-100 text-rose-700 border-rose-300', dot: 'bg-rose-500', solid: 'bg-rose-500 text-white border-rose-500' },
};

export default function DirectoryPage() {
  const [active, setActive] = useState('programs');
  const [data, setData] = useState({ programs: [], 'job-sites': [], instructors: [], 'skills-trainers': [], interns: [] });
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const results = await Promise.all(ENTITIES.map((e) => axios.get(`${API}/${e.key}`)));
      const next = {};
      ENTITIES.forEach((e, idx) => { next[e.key] = results[idx].data; });
      setData(next);
    } catch (e) {
      toast.error('Could not load the directory');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const programName = (id) => data.programs.find((x) => x.id === id)?.school_name || '—';
  const jobSiteName = (id) => data['job-sites'].find((x) => x.id === id)?.name || '—';
  const instructorName = (id) => data.instructors.find((x) => x.id === id)?.name || '—';
  const trainerName = (id) => data['skills-trainers'].find((x) => x.id === id)?.name || '—';
  const jobSitesForProgram = (pid) => (data['job-sites'] || []).filter((j) => !pid || j.program_id === pid);

  const openAdd = () => { setEditingId(null); setForm(blankForm(active)); setOpen(true); };
  const openEdit = (row) => {
    setEditingId(row.id);
    const base = blankForm(active);
    const merged = { ...base };
    Object.keys(base).forEach((k) => { merged[k] = row[k] ?? (Array.isArray(base[k]) ? [] : ''); });
    if (active === 'skills-trainers' && (!Array.isArray(merged.job_site_ids) || merged.job_site_ids.length === 0) && row.job_site_id) {
      merged.job_site_ids = [row.job_site_id];
    }
    setForm(merged);
    setOpen(true);
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/templates/upload-photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm((f) => ({ ...f, photo_url: res.data.url }));
      toast.success('Photo added');
    } catch (e) { toast.error('Could not upload photo'); }
    finally { setUploading(false); }
  };

  const save = async () => {
    const payload = { ...form };
    Object.keys(payload).forEach((k) => { if (payload[k] === NONE) payload[k] = null; });
    // required checks
    if (active === 'programs' && !payload.school_name?.trim()) return toast.error('School name is required');
    if (active === 'interns' && !payload.first_name?.trim()) return toast.error('First name is required');
    if (['job-sites', 'instructors', 'skills-trainers'].includes(active) && !payload.name?.trim()) return toast.error('Name is required');
    setBusy(true);
    try {
      if (editingId) await axios.put(`${API}/${active}/${editingId}`, payload);
      else await axios.post(`${API}/${active}`, payload);
      toast.success(editingId ? 'Saved changes' : 'Created');
      setOpen(false);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
    finally { setBusy(false); }
  };

  const remove = async (row) => {
    const label = row.name || row.school_name || row.first_name || 'this item';
    if (!window.confirm(`Remove ${label}? This can be re-added later.`)) return;
    try {
      await axios.delete(`${API}/${active}/${row.id}`);
      toast.success('Removed');
      fetchAll();
    } catch (e) { toast.error('Could not remove'); }
  };

  const rows = data[active] || [];
  const activeMeta = ENTITIES.find((e) => e.key === active);

  const initials = (row) => {
    const n = row.name || row.school_name || `${row.first_name || ''} ${row.last_name || ''}`;
    return n.trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase();
  };

  return (
    <div className="space-y-6" data-testid="directory-page">
      <div>
        <h1 className="text-2xl font-bold font-['Manrope'] text-[#2f3a40]">Program Directory</h1>
        <p className="text-sm text-[#5C5C5C] mt-1">Programs, job sites, instructors, skills trainers and interns — the backbone for job reports.</p>
      </div>

      {/* Entity tabs */}
      <div className="flex flex-wrap gap-2">
        {ENTITIES.map((e) => {
          const Icon = e.icon;
          const count = (data[e.key] || []).length;
          return (
            <button
              key={e.key}
              onClick={() => setActive(e.key)}
              data-testid={`dir-tab-${e.key}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${active === e.key ? 'bg-[#34657f] text-white' : 'bg-white text-[#34657f] border border-slate-200 hover:bg-slate-50'}`}
            >
              <Icon className="w-4 h-4" /> {e.label}
              <span className={`text-xs px-1.5 rounded-full ${active === e.key ? 'bg-white/20' : 'bg-slate-100'}`}>{count}</span>
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-[#2f3a40]">{activeMeta.label}</h2>
        <Button onClick={openAdd} className="bg-[#e87722] hover:bg-[#cf6a1e]" data-testid="dir-add-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Add {activeMeta.singular}
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-[#5C5C5C] py-10 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-center py-12 text-[#5C5C5C] bg-white rounded-xl border border-dashed border-slate-200">No {activeMeta.label.toLowerCase()} yet. Click “Add {activeMeta.singular}”.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {rows.map((row) => (
            <div key={row.id} className="bg-white rounded-xl border border-slate-200 p-4 flex gap-3" data-testid={`dir-row-${row.id}`}>
              <Avatar className="h-12 w-12 shrink-0">
                <AvatarImage src={row.photo_url} />
                <AvatarFallback className="bg-[#34657f]/10 text-[#34657f] font-semibold">{initials(row)}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                {active === 'programs' && (<>
                  <p className="font-semibold text-[#2f3a40] truncate">{row.school_name}</p>
                  <p className="text-xs text-[#5C5C5C] truncate">{[row.city, row.state].filter(Boolean).join(', ') || row.geography || 'No location'}</p>
                  <p className="text-xs text-[#5C5C5C] truncate">Lead: {row.lead_contact || '—'}{row.phone ? ` · ${row.phone}` : ''}</p>
                  {(row.program_types || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {row.program_types.map((t) => (<span key={t} className="text-[10px] bg-[#34657f]/10 text-[#34657f] px-1.5 py-0.5 rounded-full">{t}</span>))}
                    </div>
                  )}
                  {row.project_search === 'Yes' && (<span className="inline-block mt-1 text-[10px] bg-[#e87722]/15 text-[#b45309] px-1.5 py-0.5 rounded-full">Project SEARCH</span>)}
                </>)}
                {active === 'job-sites' && (<>
                  <p className="font-semibold text-[#2f3a40] truncate">{row.name}</p>
                  <p className="text-xs text-[#5C5C5C] truncate">{programName(row.program_id)}</p>
                  <p className="text-xs text-[#5C5C5C] truncate">{row.contact || '—'}{row.phone ? ` · ${row.phone}` : ''}</p>
                  {row.address && <p className="text-xs text-[#5C5C5C] truncate">{row.address}</p>}
                  {(row.tasks || []).length > 0 && (
                    <div className="mt-1.5 space-y-1" data-testid={`dir-tasks-${row.id}`}>
                      {row.tasks.map((t, i) => (
                        <div key={i} className={`flex items-center gap-1.5 text-[11px] border rounded-md px-1.5 py-1 ${COMPLEXITY[t.complexity]?.chip || COMPLEXITY[1].chip}`}>
                          <span className={`w-2 h-2 rounded-full shrink-0 ${COMPLEXITY[t.complexity]?.dot || COMPLEXITY[1].dot}`} />
                          <span className="truncate flex-1">{t.description}</span>
                          <span className="font-semibold shrink-0">L{t.complexity}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>)}
                {active === 'instructors' && (<>
                  <p className="font-semibold text-[#2f3a40] truncate">{row.name}</p>
                  <p className="text-xs text-[#5C5C5C] truncate">{programName(row.program_id)}</p>
                  <p className="text-xs text-[#5C5C5C] truncate">{row.email || '—'}</p>
                </>)}
                {active === 'skills-trainers' && (<>
                  <p className="font-semibold text-[#2f3a40] truncate">{row.name}</p>
                  <p className="text-xs text-[#5C5C5C] truncate">{programName(row.program_id)}</p>
                  <p className="text-xs text-[#5C5C5C] truncate">Job sites: {(row.job_site_ids && row.job_site_ids.length ? row.job_site_ids : (row.job_site_id ? [row.job_site_id] : [])).map(jobSiteName).join(', ') || '—'}</p>
                  <p className="text-xs text-[#5C5C5C] truncate">{row.email || '—'}</p>
                </>)}
                {active === 'interns' && (<>
                  <p className="font-semibold text-[#2f3a40] truncate">{row.name}{row.age != null ? <span className="text-xs text-[#5C5C5C] font-normal"> · Age {row.age}</span> : ''}</p>
                  <p className="text-xs text-[#5C5C5C] truncate">Job site: {jobSiteName(row.job_site_id)}</p>
                  <p className="text-xs text-[#5C5C5C] truncate">Instructor: {instructorName(row.instructor_id)}</p>
                  <p className="text-xs text-[#5C5C5C] truncate">Trainer: {trainerName(row.skills_trainer_id)}</p>
                  {!row.privileged && <p className="text-[10px] text-amber-600 mt-0.5">Limited view (name & age only)</p>}
                </>)}
              </div>
              <div className="flex flex-col gap-1">
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(row)} data-testid={`dir-edit-${row.id}`}><Pencil className="w-4 h-4 text-[#34657f]" /></Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => remove(row)} data-testid={`dir-delete-${row.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add / Edit dialog */}
      <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) setOpen(false); }}>
        <DialogContent className="sm:max-w-lg max-h-[88vh] overflow-y-auto" data-testid="dir-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">{editingId ? 'Edit' : 'Add'} {activeMeta.singular}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            {/* Photo (all entities) */}
            {(
              <div className="space-y-1.5">
                <Label>Photo (optional)</Label>
                {form.photo_url ? (
                  <div className="flex items-center gap-2 text-sm text-[#3d7a22] h-10 px-3 rounded-md border border-slate-200">
                    <ImagePlus className="w-4 h-4" /> Photo attached
                    <button type="button" className="text-red-600 underline text-xs ml-auto" onClick={() => setForm((f) => ({ ...f, photo_url: '' }))}><X className="w-3.5 h-3.5 inline" /> remove</button>
                  </div>
                ) : (
                  <label className="flex items-center gap-2 h-10 px-3 rounded-md border border-dashed border-slate-300 text-sm text-[#5C5C5C] cursor-pointer hover:border-[#34657f]">
                    <ImagePlus className="w-4 h-4 text-[#34657f]" />{uploading ? 'Uploading…' : 'Add a photo'}
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(e.target.files?.[0])} data-testid="dir-photo-input" />
                  </label>
                )}
              </div>
            )}

            {active === 'programs' && (<>
              <Field label="Program name *" v={form.school_name} on={(x) => setForm({ ...form, school_name: x })} tid="f-school_name" />
              <div className="grid grid-cols-2 gap-3">
                <Field label="City" v={form.city} on={(x) => setForm({ ...form, city: x })} tid="f-city" />
                <Field label="State" v={form.state} on={(x) => setForm({ ...form, state: x })} tid="f-state" placeholder="e.g. OH" />
              </div>
              <Field label="Lead contact name" v={form.lead_contact} on={(x) => setForm({ ...form, lead_contact: x })} tid="f-lead_contact" />
              <Field label="Phone number" v={form.phone} on={(x) => setForm({ ...form, phone: x })} tid="f-phone" placeholder="e.g. (513) 555-0100" />
              <div className="space-y-1.5">
                <Label>Project SEARCH site?</Label>
                <Select value={form.project_search || 'No'} onValueChange={(x) => setForm({ ...form, project_search: x })}>
                  <SelectTrigger data-testid="f-project_search"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Yes">Yes</SelectItem>
                    <SelectItem value="No">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Program type</Label>
                <div className="flex flex-wrap gap-2">
                  {PROGRAM_TYPES.map((t) => {
                    const on = (form.program_types || []).includes(t);
                    return (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, program_types: on ? (f.program_types || []).filter((x) => x !== t) : [...(f.program_types || []), t] }))}
                        data-testid={`f-ptype-${t}`}
                        className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${on ? 'bg-[#34657f] text-white border-[#34657f]' : 'bg-white text-[#34657f] border-slate-300 hover:bg-slate-50'}`}
                      >
                        {on ? '✓ ' : ''}{t}
                      </button>
                    );
                  })}
                </div>
              </div>
            </>)}

            {active === 'job-sites' && (<>
              <Field label="Job site name *" v={form.name} on={(x) => setForm({ ...form, name: x })} tid="f-name" />
              <SelectField label="Program" v={form.program_id} on={(x) => setForm({ ...form, program_id: x })} options={data.programs.map((p) => ({ value: p.id, label: p.school_name }))} tid="f-program_id" />
              <Field label="Contact" v={form.contact} on={(x) => setForm({ ...form, contact: x })} tid="f-contact" />
              <Field label="Address" v={form.address} on={(x) => setForm({ ...form, address: x })} tid="f-address" />
              <Field label="Phone" v={form.phone} on={(x) => setForm({ ...form, phone: x })} tid="f-phone" />
              <div className="space-y-1.5"><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="f-description" rows={3} /></div>
              <TaskEditor
                tasks={Array.isArray(form.tasks) ? form.tasks : []}
                onChange={(tasks) => setForm((f) => ({ ...f, tasks }))}
              />
            </>)}

            {active === 'instructors' && (<>
              <Field label="Name *" v={form.name} on={(x) => setForm({ ...form, name: x })} tid="f-name" />
              <Field label="Email" v={form.email} on={(x) => setForm({ ...form, email: x })} tid="f-email" />
              <SelectField label="Program" v={form.program_id} on={(x) => setForm({ ...form, program_id: x })} options={data.programs.map((p) => ({ value: p.id, label: p.school_name }))} tid="f-program_id" />
            </>)}

            {active === 'skills-trainers' && (<>
              <Field label="Name *" v={form.name} on={(x) => setForm({ ...form, name: x })} tid="f-name" />
              <Field label="Email" v={form.email} on={(x) => setForm({ ...form, email: x })} tid="f-email" />
              <SelectField label="Program" v={form.program_id} on={(x) => setForm({ ...form, program_id: x })} options={data.programs.map((p) => ({ value: p.id, label: p.school_name }))} tid="f-program_id" />
              <MultiJobSiteField
                label="Job sites (choose one or more)"
                selected={Array.isArray(form.job_site_ids) ? form.job_site_ids : []}
                onChange={(ids) => setForm((f) => ({ ...f, job_site_ids: ids }))}
                options={jobSitesForProgram(form.program_id)}
              />
            </>)}

            {active === 'interns' && (<>
              <div className="grid grid-cols-2 gap-3">
                <Field label="First name *" v={form.first_name} on={(x) => setForm({ ...form, first_name: x })} tid="f-first_name" />
                <Field label="Last name" v={form.last_name} on={(x) => setForm({ ...form, last_name: x })} tid="f-last_name" />
              </div>
              <p className="text-xs text-[#5C5C5C] -mt-1">Others see first name + last initial only. Full name, DOB &amp; case notes are visible to this program's admins/instructors only.</p>
              <Field label="Email" v={form.email} on={(x) => setForm({ ...form, email: x })} tid="f-email" />
              <div className="space-y-1.5"><Label>Date of birth (shows as Age to everyone)</Label><Input type="date" value={form.dob || ''} onChange={(e) => setForm({ ...form, dob: e.target.value })} data-testid="f-dob" /></div>
              <SelectField label="Program" v={form.program_id} on={(x) => setForm({ ...form, program_id: x })} options={data.programs.map((p) => ({ value: p.id, label: p.school_name }))} tid="f-program_id" />
              <div className="space-y-1.5">
                <Label>Federal disability category</Label>
                <Select value={form.disability_category || '__none__'} onValueChange={(x) => setForm({ ...form, disability_category: x === '__none__' ? '' : x })}>
                  <SelectTrigger data-testid="f-disability_category"><SelectValue placeholder="Select a category…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None / not specified</SelectItem>
                    {DISABILITY_CATEGORIES.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <Field label="Dietary restriction (optional)" v={form.dietary_restriction} on={(x) => setForm({ ...form, dietary_restriction: x })} tid="f-dietary_restriction" placeholder="e.g. Nut allergy, vegetarian" />
              <Field label="Physical restrictions (optional)" v={form.physical_restrictions} on={(x) => setForm({ ...form, physical_restrictions: x })} tid="f-physical_restrictions" placeholder="e.g. No heavy lifting over 10 lbs" />
              <Field label="Important preferences (optional)" v={form.preferences} on={(x) => setForm({ ...form, preferences: x })} tid="f-preferences" placeholder="e.g. Prefers quiet environments" />
              <div className="pt-1"><p className="text-sm font-semibold text-[#2f3a40]">Job site rotations</p><p className="text-xs text-[#5C5C5C]">Chosen from job sites under this intern's program.</p></div>
              <SelectField label="Rotation 1 job site" v={form.rotation_1_job_site_id} on={(x) => setForm({ ...form, rotation_1_job_site_id: x })} options={jobSitesForProgram(form.program_id).map((j) => ({ value: j.id, label: j.name }))} tid="f-rotation_1" />
              <SelectField label="Rotation 2 job site" v={form.rotation_2_job_site_id} on={(x) => setForm({ ...form, rotation_2_job_site_id: x })} options={jobSitesForProgram(form.program_id).map((j) => ({ value: j.id, label: j.name }))} tid="f-rotation_2" />
              <SelectField label="Rotation 3 job site" v={form.rotation_3_job_site_id} on={(x) => setForm({ ...form, rotation_3_job_site_id: x })} options={jobSitesForProgram(form.program_id).map((j) => ({ value: j.id, label: j.name }))} tid="f-rotation_3" />
              <div className="pt-1"><p className="text-sm font-semibold text-[#2f3a40]">Current assignment</p></div>
              <SelectField label="Current job site" v={form.job_site_id} on={(x) => setForm({ ...form, job_site_id: x })} options={jobSitesForProgram(form.program_id).map((j) => ({ value: j.id, label: j.name }))} tid="f-job_site_id" />
              <SelectField label="Current instructor" v={form.instructor_id} on={(x) => setForm({ ...form, instructor_id: x })} options={data.instructors.map((i) => ({ value: i.id, label: i.name }))} tid="f-instructor_id" />
              <SelectField label="Current skills trainer" v={form.skills_trainer_id} on={(x) => setForm({ ...form, skills_trainer_id: x })} options={data['skills-trainers'].map((s) => ({ value: s.id, label: s.name }))} tid="f-skills_trainer_id" />
              <div className="space-y-1.5"><Label>Case study notes (private)</Label><Textarea value={form.case_study_notes} onChange={(e) => setForm({ ...form, case_study_notes: e.target.value })} data-testid="f-case_study_notes" rows={3} placeholder="Do not include other students' names here." /></div>
            </>)}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={save} disabled={busy} className="bg-[#34657f] hover:bg-[#2a5060]" data-testid="dir-save-btn">{busy ? 'Saving…' : (editingId ? 'Save changes' : 'Create')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, v, on, tid, placeholder }) => (
  <div className="space-y-1.5">
    <Label>{label}</Label>
    <Input value={v || ''} onChange={(e) => on(e.target.value)} data-testid={tid} placeholder={placeholder} />
  </div>
);

const SelectField = ({ label, v, on, options, tid }) => (
  <div className="space-y-1.5">
    <Label>{label}</Label>
    <Select value={v || '__none__'} onValueChange={(x) => on(x === '__none__' ? '' : x)}>
      <SelectTrigger data-testid={tid}><SelectValue placeholder="None" /></SelectTrigger>
      <SelectContent>
        <SelectItem value="__none__">None</SelectItem>
        {options.map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}
      </SelectContent>
    </Select>
  </div>
);

const MultiJobSiteField = ({ label, selected, onChange, options }) => {
  const toggle = (id) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {options.length === 0 ? (
        <p className="text-xs text-[#5C5C5C] border border-dashed border-slate-300 rounded-md px-3 py-2">No job sites for this program yet. Add job sites first (or pick a program above).</p>
      ) : (
        <div className="flex flex-wrap gap-2" data-testid="f-job_site_ids">
          {options.map((j) => {
            const on = selected.includes(j.id);
            return (
              <button
                key={j.id}
                type="button"
                onClick={() => toggle(j.id)}
                data-testid={`f-jobsite-${j.id}`}
                className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${on ? 'bg-[#34657f] text-white border-[#34657f]' : 'bg-white text-[#34657f] border-slate-300 hover:bg-slate-50'}`}
              >
                {on ? '✓ ' : ''}{j.name}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const TASK_COMPLEXITY = {
  1: { label: 'Level 1', solid: 'bg-emerald-500 text-white border-emerald-500', dot: 'bg-emerald-500' },
  2: { label: 'Level 2', solid: 'bg-amber-500 text-white border-amber-500', dot: 'bg-amber-500' },
  3: { label: 'Level 3', solid: 'bg-rose-500 text-white border-rose-500', dot: 'bg-rose-500' },
};

const TaskEditor = ({ tasks, onChange }) => {
  const update = (i, patch) => onChange(tasks.map((t, idx) => (idx === i ? { ...t, ...patch } : t)));
  const add = () => onChange([...tasks, { description: '', complexity: 1 }]);
  const remove = (i) => onChange(tasks.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>Job site tasks (up to 6)</Label>
        <span className="text-xs text-[#5C5C5C]">Complexity: <span className="text-emerald-600 font-medium">1 simple</span> · <span className="text-amber-600 font-medium">2 moderate</span> · <span className="text-rose-600 font-medium">3 complex</span></span>
      </div>
      {tasks.map((t, i) => (
        <div key={i} className="flex items-start gap-2 border border-slate-200 rounded-md p-2" data-testid={`f-task-${i}`}>
          <span className={`mt-2.5 w-2.5 h-2.5 rounded-full shrink-0 ${TASK_COMPLEXITY[t.complexity]?.dot || TASK_COMPLEXITY[1].dot}`} />
          <Input
            className="flex-1"
            value={t.description || ''}
            placeholder={`Task ${i + 1} — e.g. Stocking dairy shelves`}
            onChange={(e) => update(i, { description: e.target.value })}
            data-testid={`f-task-desc-${i}`}
          />
          <div className="flex gap-1 shrink-0">
            {[1, 2, 3].map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => update(i, { complexity: c })}
                data-testid={`f-task-cx-${i}-${c}`}
                title={`Complexity ${c}`}
                className={`w-8 h-9 rounded text-sm font-bold border transition-colors ${t.complexity === c ? TASK_COMPLEXITY[c].solid : 'bg-white border-slate-300 text-slate-500 hover:border-slate-400'}`}
              >
                {c}
              </button>
            ))}
            <Button type="button" variant="ghost" size="icon" className="h-9 w-8 text-red-500" onClick={() => remove(i)} data-testid={`f-task-remove-${i}`}><X className="w-4 h-4" /></Button>
          </div>
        </div>
      ))}
      {tasks.length < 6 && (
        <Button type="button" variant="outline" size="sm" className="border-dashed" onClick={add} data-testid="f-task-add">
          <Plus className="w-4 h-4 mr-1" /> Add task ({tasks.length}/6)
        </Button>
      )}
    </div>
  );
};
